import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { SchedulingNavComponent } from '../../../shared/scheduling-nav/scheduling-nav.component';
import { SchedulingService } from '../../services/scheduling.service';
import {
  ApiPagedResponse,
  CreateScheduleGroupRequest,
  CurrentUser,
  DayOfWeekName,
  ScheduleGroupDetails,
  ScheduleGroupListItem,
  ShiftListItem,
  UserFilterValues,
  UserListItem,
  UserRef,
} from '../../models/scheduling.model';
import { FilterService } from '../../../services/filter.service';

type ToastKind = 'success' | 'error' | 'info';
type ConfirmKind = 'default' | 'danger';
type ModalMode = 'create' | 'rename' | 'assign';
type ConfirmAction = 'rename' | 'removeDay' | 'assign';

interface ScheduleGroupRowVm extends ScheduleGroupListItem {
  createdByLabel: string;
  departmentLabel: string;
  createdAtLabel: string;
  primaryDepartmentId: number | null;
  primaryCompanyId: number | null;
}

interface DayRowVm {
  id: number;
  dayOfWeek: DayOfWeekName;
  startTime: string;
  endTime: string;
  isOverNight: boolean;
}

interface EmployeeChipVm {
  id: number;
  label: string;
  employeeNumber?: string | null;
  departmentId?: number | null;
  companyId?: number | null;
}

type ConfirmPayload =
  | { kind: 'removeDay'; groupId: number; scheduleWithDayId: number }
  | { kind: 'rename'; groupId: number; nextName: string; prevName: string }
  | {
    kind: 'assign';
    scheduleGroupId: number;
    employeeIds: number[];
    scheduleName: string;
    departmentId: number;
    departmentLabel?: string;
  };

const DAY_ORDER: DayOfWeekName[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];
const COLOR_BUCKET_COUNT = 8;

type ColorEntity = 'employee' | 'department' | 'company' | 'schedule';

@Component({
  selector: 'app-schedule-groups',
  standalone: true,
  imports: [CommonModule, RouterModule, SchedulingNavComponent],
  templateUrl: './schedule-groups.component.html',
  styleUrls: ['./schedule-groups.component.scss'],
})
export class ScheduleGroupsComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  filterService = inject(FilterService);

  private listDriveSub: Subscription | null = null;
  private detailsSub: Subscription | null = null;

  scheduleSearch = '';
  private readonly scheduleSearchInput$ = new Subject<string>();
  private readonly scheduleSearchTerm$ = new BehaviorSubject<string>('');

  isLoadingList = false;
  isLoadingDetails = false;
  isSaving = false;
  errorMessage: string | null = null;

  toastMessage: string | null = null;
  toastKind: ToastKind = 'info';
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  confirmOpen = false;
  confirmTitle = 'Confirm';
  confirmMessage = '';
  confirmOkText = 'Confirm';
  confirmCancelText = 'Cancel';
  confirmKind: ConfirmKind = 'default';
  private confirmAction: ConfirmAction | null = null;
  private confirmPayload: ConfirmPayload | null = null;

  scheduleGroups$!: Observable<ScheduleGroupRowVm[]>;
  currentUser$!: Observable<CurrentUser>;

  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages = 1;
  readonly pageSizeOptions = [10, 20, 50];

  selectedScheduleGroupId: number | null = null;
  selectedScheduleGroupDetails: ScheduleGroupDetails | null = null;
  selectedDayRows: DayRowVm[] = [];

  selectedScheduleColorClass = 'color-0';

  employeeSearch = '';
  showAllEmployees = false;
  readonly maxVisibleEmployees = 60;

  private assignedEmployeesAll: EmployeeChipVm[] = [];
  assignedEmployees: EmployeeChipVm[] = [];

  assignSearch = '';
  assignIsLoadingFilters = false;
  assignIsLoadingUsers = false;

  assignFilters: UserFilterValues | null = null;

  assignCompanyId: number | null = null;
  assignDepartmentFilterId: number | null = null;
  assignWorkerTypeId: number | null = null;
  assignShiftId: number | null = null;

  assignDepartmentForScheduleId: number | null = null;

  assignUsers: UserListItem[] = [];
  assignPage = 1;
  assignPageSize = 25;
  assignTotalCount = 0;
  assignTotalPages = 1;

  private readonly selectedEmployeeIds = new Set<number>();
  private readonly openDepartmentIds = new Set<number>();

  isModalOpen = false;
  modalMode: ModalMode = 'create';

  createName = '';
  createShiftId: number | null = null;
  createDepartmentId: number | null = null;

  readonly createTimeZoneId =
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';

  createDays: Array<{
    dayOfWeek: DayOfWeekName;
    startTime: string;
    endTime: string;
    enabled: boolean;
  }> = this.getDefaultCreateDays(false);

  renameValue = '';
  renameOriginal = '';
  isRenaming = false;

  constructor(
    private readonly schedulingService: SchedulingService,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.bindScheduleSearch();
    this.loadCore();
  }

  ngOnDestroy(): void {
    this.lockBodyScroll(false);
    this.listDriveSub?.unsubscribe();
    this.detailsSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.clearToast();
  }

  get totalEmployees(): number {
    return this.assignedEmployeesAll.length;
  }

  get filteredEmployeesCount(): number {
    return this.assignedEmployees.length;
  }

  get visibleEmployees(): EmployeeChipVm[] {
    return this.showAllEmployees
      ? this.assignedEmployees
      : this.assignedEmployees.slice(0, this.maxVisibleEmployees);
  }

  get selectedAssignCount(): number {
    return this.selectedEmployeeIds.size;
  }

  get isAssignSaveDisabled(): boolean {
    return (
      this.isSaving ||
      this.assignIsLoadingUsers ||
      this.selectedEmployeeIds.size === 0 ||
      !this.assignDepartmentForScheduleId
    );
  }

  get filteredEmployees(): EmployeeChipVm[] {
    const query = (this.employeeSearch || '').trim().toLowerCase();
    if (!query) return this.assignedEmployees;

    return this.assignedEmployees.filter((employee) => {
      const name = (employee.label || '').toLowerCase();
      const number = (employee.employeeNumber || '').toString().toLowerCase();
      return name.includes(query) || number.includes(query);
    });
  }

  get hasAnyCreateDaysEnabled(): boolean {
    return this.createDays.some((day) => !!day.enabled);
  }

  get canCreateSchedule(): boolean {
    return (
      !!this.createName.trim() &&
      !!this.createShiftId &&
      !!this.createDepartmentId &&
      this.hasAnyCreateDaysEnabled &&
      !this.isSaving
    );
  }

  isDepartmentOpen(id: number): boolean {
    return this.openDepartmentIds.has(id);
  }

  toggleDepartment(id: number): void {
    if (!id) return;

    if (this.openDepartmentIds.has(id)) {
      this.openDepartmentIds.delete(id);
    } else {
      this.openDepartmentIds.add(id);
    }

    this.cdr.markForCheck();
  }

  private loadCore(): void {
    this.errorMessage = null;

    this.currentUser$ = this.schedulingService.getCurrentUser().pipe(
      tap((user) => {
        this.createDepartmentId = user?.department?.id ?? null;
      }),
      shareReplay(1),
      catchError(() => {
        this.errorMessage = 'Failed to load current user';
        this.cdr.markForCheck();
        return of(this.emptyCurrentUser());
      }),
    );

    this.loadScheduleGroups(true);
  }

  loadScheduleGroups(autoSelectFirst: boolean): void {
    this.errorMessage = null;
    this.isLoadingList = true;
    this.cdr.markForCheck();

    const query = {
      page: this.currentPage,
      pageSize: this.pageSize,
    };

    const request$ = this.schedulingService.getScheduleGroups(query).pipe(
      tap((response: ApiPagedResponse<ScheduleGroupListItem>) => {
        this.totalCount = Number(response?.totalCount ?? 0) || 0;
        this.totalPages = Number(response?.totalPages ?? 1) || 1;
        this.currentPage = Number(response?.currentPage ?? this.currentPage) || this.currentPage;
        this.pageSize = Number(response?.pageSize ?? this.pageSize) || this.pageSize;
      }),
      map((response: ApiPagedResponse<ScheduleGroupListItem>) =>
        (response?.data ?? []).map((group) => this.toRowVm(group)),
      ),
      catchError(() => {
        this.errorMessage = 'Failed to load schedules';
        return of([] as ScheduleGroupRowVm[]);
      }),
      finalize(() => {
        this.isLoadingList = false;
        this.cdr.markForCheck();
      }),
      shareReplay(1),
      takeUntil(this.destroy$),
    );

    this.scheduleGroups$ = combineLatest([request$, this.scheduleSearchTerm$]).pipe(
      map(([rows, searchTerm]) => this.filterScheduleGroups(rows, searchTerm)),
      shareReplay(1),
      takeUntil(this.destroy$),
    );
    this.listDriveSub?.unsubscribe();
    this.listDriveSub = request$
      .pipe(
        tap((rows) => {
          if (!rows.length) {
            this.selectedScheduleGroupId = null;
            this.selectedScheduleGroupDetails = null;
            this.selectedDayRows = [];
            this.setEmployees([]);
            this.resetOpenDepartments();
            return;
          }

          if (autoSelectFirst && !this.selectedScheduleGroupId) {
            this.openScheduleGroup(rows[0].id, 0);
            return;
          }

          const stillExists = this.selectedScheduleGroupId
            ? rows.some((row) => row.id === this.selectedScheduleGroupId)
            : false;

          if (autoSelectFirst && (!stillExists || !this.selectedScheduleGroupId)) {
            this.openScheduleGroup(rows[0].id, 0);
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  refreshScheduleGroups(): void {
    this.loadScheduleGroups(false);

    if (this.selectedScheduleGroupId) {
      this.openScheduleGroup(this.selectedScheduleGroupId);
    }
  }

  goToPage(page: number): void {
    const nextPage = Math.min(Math.max(1, page), this.totalPages || 1);
    if (nextPage === this.currentPage) return;

    this.currentPage = nextPage;
    this.loadScheduleGroups(true);
  }

  changePageSize(raw: string | number): void {
    const nextSize = typeof raw === 'string' ? parseInt(raw, 10) : raw;
    if (!nextSize || nextSize === this.pageSize) return;

    this.pageSize = nextSize;
    this.currentPage = 1;
    this.loadScheduleGroups(true);
  }

  openScheduleGroup(id: number, colorIndex?: number): void {
    if (!id) return;

    this.selectedScheduleColorClass = this.getScheduleColorClassById(id);

    this.selectedScheduleGroupId = id;
    this.errorMessage = null;
    this.isLoadingDetails = true;
    this.cdr.markForCheck();

    this.detailsSub?.unsubscribe();
    this.detailsSub = this.schedulingService
      .getScheduleGroupById(id)
      .pipe(
        map((response) => response?.data ?? null),
        tap((details) => {
          this.selectedScheduleGroupDetails = details;
          this.selectedDayRows = this.buildCompactDayRows(details);
          this.setEmployees(this.mapEmployeesFromDetails(details));
          this.setDefaultOpenDepartments(details);
        }),
        catchError(() => {
          this.errorMessage = 'Failed to load schedule details';
          this.selectedScheduleGroupDetails = null;
          this.selectedDayRows = [];
          this.setEmployees([]);
          this.resetOpenDepartments();
          return of(null);
        }),
        finalize(() => {
          this.isLoadingDetails = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  onEmployeeSearchChange(raw: string): void {
    this.employeeSearch = raw ?? '';
    this.applyEmployeeFilter();
  }

  toggleShowAllEmployees(): void {
    this.showAllEmployees = !this.showAllEmployees;
    this.cdr.markForCheck();
  }

  private setEmployees(all: EmployeeChipVm[]): void {
    this.assignedEmployeesAll = all ?? [];
    this.showAllEmployees = false;
    this.employeeSearch = '';
    this.applyEmployeeFilter();
  }

  private applyEmployeeFilter(): void {
    const term = (this.employeeSearch ?? '').trim().toLowerCase();

    if (!term) {
      this.assignedEmployees = [...this.assignedEmployeesAll];
      this.cdr.markForCheck();
      return;
    }

    this.assignedEmployees = this.assignedEmployeesAll.filter((employee) => {
      const label = (employee.label ?? '').toLowerCase();
      const number = (employee.employeeNumber ?? '').toLowerCase();
      return label.includes(term) || number.includes(term);
    });

    this.cdr.markForCheck();
  }

  requestRemoveDay(scheduleWithDayId: number): void {
    const groupId = this.selectedScheduleGroupId;
    if (!groupId || !scheduleWithDayId) return;

    this.confirmAction = 'removeDay';
    this.confirmPayload = { kind: 'removeDay', groupId, scheduleWithDayId };
    this.confirmTitle = 'Remove day';
    this.confirmMessage = 'Remove this day from the schedule group?\n\nThis cannot be undone.';
    this.confirmOkText = 'Remove';
    this.confirmCancelText = 'Cancel';
    this.confirmKind = 'danger';
    this.confirmOpen = true;

    this.lockBodyScroll(true);
    this.cdr.markForCheck();
  }

  requestRenameConfirm(): void {
    const groupId = this.selectedScheduleGroupId;
    if (!groupId) return;

    const nextName = this.renameValue.trim();
    const prevName = this.renameOriginal.trim();

    if (!nextName) {
      this.showToast('error', 'Name cannot be empty.');
      return;
    }

    if (nextName === prevName) {
      this.closeModal();
      return;
    }

    this.confirmAction = 'rename';
    this.confirmPayload = { kind: 'rename', groupId, nextName, prevName };
    this.confirmTitle = 'Save changes';
    this.confirmMessage = `Save name change?\n\n"${prevName}" → "${nextName}"`;
    this.confirmOkText = 'Save';
    this.confirmCancelText = 'Cancel';
    this.confirmKind = 'default';
    this.confirmOpen = true;

    this.lockBodyScroll(true);
    this.cdr.markForCheck();
  }

  requestAssignConfirm(): void {
    const scheduleGroupId = this.selectedScheduleGroupDetails?.id;
    if (!scheduleGroupId) return;

    const employeeIds = Array.from(this.selectedEmployeeIds);
    if (!employeeIds.length) {
      this.showToast('error', 'Select at least one employee.');
      return;
    }

    const departmentId = this.assignDepartmentForScheduleId;
    if (!departmentId) {
      this.showToast(
        'error',
        'Department is required.\n\nSelect the department that these employees should be scheduled under.',
      );
      return;
    }

    const scheduleName = this.selectedScheduleGroupDetails?.name ?? `Schedule #${scheduleGroupId}`;
    const departmentLabel =
      (this.assignFilters?.departments ?? ([] as any[])).find(
        (department: any) => Number(department?.id) === Number(departmentId),
      )?.name ?? `Department #${departmentId}`;

    this.confirmAction = 'assign';
    this.confirmPayload = {
      kind: 'assign',
      scheduleGroupId,
      employeeIds,
      scheduleName,
      departmentId,
      departmentLabel,
    };
    this.confirmTitle = 'Assign schedule';
    this.confirmMessage = `Assign "${scheduleName}" to ${employeeIds.length} employee(s)\nunder ${departmentLabel}?`;
    this.confirmOkText = 'Assign';
    this.confirmCancelText = 'Cancel';
    this.confirmKind = 'default';
    this.confirmOpen = true;

    this.lockBodyScroll(true);
    this.cdr.markForCheck();
  }

  onConfirmCancel(): void {
    this.confirmOpen = false;
    this.confirmAction = null;
    this.confirmPayload = null;

    this.lockBodyScroll(this.isModalOpen);
    this.cdr.markForCheck();
  }

  onConfirmOk(): void {
    const action = this.confirmAction;
    const payload = this.confirmPayload;

    this.onConfirmCancel();

    if (!action || !payload) return;

    if (action === 'removeDay' && payload.kind === 'removeDay') {
      this.performRemoveDay(payload.groupId, payload.scheduleWithDayId);
      return;
    }

    if (action === 'rename' && payload.kind === 'rename') {
      this.performRename(payload.groupId, payload.nextName);
      return;
    }

    if (action === 'assign' && payload.kind === 'assign') {
      this.performAssign(payload.scheduleGroupId, payload.departmentId, payload.employeeIds);
    }
  }

  private performRemoveDay(groupId: number, scheduleWithDayId: number): void {
    this.errorMessage = null;
    this.isSaving = true;
    this.cdr.markForCheck();

    this.schedulingService
      .removeScheduleWithDay(groupId, scheduleWithDayId)
      .pipe(
        switchMap(() => this.schedulingService.getScheduleGroupById(groupId)),
        map((response) => response?.data ?? null),
        tap((details) => {
          this.selectedScheduleGroupDetails = details;
          this.selectedDayRows = this.buildCompactDayRows(details);
          this.setEmployees(this.mapEmployeesFromDetails(details));
          this.setDefaultOpenDepartments(details);
          this.showToast('success', 'Day removed.');
        }),
        tap(() => this.loadScheduleGroups(false)),
        catchError(() => {
          this.showToast('error', 'Failed to remove day.');
          return of(null);
        }),
        finalize(() => {
          this.isSaving = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private performRename(groupId: number, name: string): void {
    this.isRenaming = true;
    this.isSaving = true;
    this.errorMessage = null;
    this.cdr.markForCheck();

    this.schedulingService
      .patchScheduleGroupName(groupId, name)
      .pipe(
        tap((response) => {
          const updated = (response as any)?.data;
          if (updated) {
            this.selectedScheduleGroupDetails = updated;
            this.setDefaultOpenDepartments(updated);
          }
        }),
        tap(() => {
          this.closeModal();
          this.showToast('success', 'Name updated.');
        }),
        tap(() => this.loadScheduleGroups(false)),
        tap(() => this.openScheduleGroup(groupId)),
        catchError(() => {
          this.showToast('error', 'Failed to update name.');
          return of(null);
        }),
        finalize(() => {
          this.isRenaming = false;
          this.isSaving = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private performAssign(
    scheduleGroupId: number,
    departmentId: number,
    employeeIds: number[],
  ): void {
    if (!departmentId) {
      this.showToast('error', 'Please select a department.');
      return;
    }

    if (!employeeIds.length) {
      this.showToast('error', 'Select at least one employee.');
      return;
    }

    this.isSaving = true;
    this.errorMessage = null;
    this.cdr.markForCheck();

    this.schedulingService
      .assignScheduleGroupToEmployees(scheduleGroupId, departmentId, employeeIds)
      .pipe(
        tap(() => {
          this.closeModal();
          this.showToast('success', 'Schedule assigned.');
        }),
        tap(() => this.loadScheduleGroups(false)),
        tap(() => this.openScheduleGroup(scheduleGroupId)),
        catchError((err) => {
          const status = Number(err?.status ?? err?.error?.status ?? 0);

          if (status === 405) {
            this.showToast('error', 'Assign endpoint not available (405).');
            return of(null);
          }

          if (this.isMissingDepartmentError(err)) {
            this.showToast('error', 'Please select a department.');
          } else {
            this.showToast('error', 'Failed to assign schedule.');
          }

          return of(null);
        }),
        finalize(() => {
          this.isSaving = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  formatScheduleDisplayTime(value: string | null | undefined): string {
    if (!value || value === '—') return '—';

    const match = value.trim().match(/^(\d{1,2}):(\d{2})/);
    if (!match) return value;

    let hours = Number(match[1]);
    const minutes = match[2];

    if (Number.isNaN(hours)) return value;

    const suffix = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;

    return `${hours}:${minutes} ${suffix}`;
  }

  openAssignModal(): void {
    if (!this.selectedScheduleGroupDetails?.id) return;

    this.modalMode = 'assign';
    this.isModalOpen = true;
    this.errorMessage = null;
    this.lockBodyScroll(true);

    this.assignSearch = '';
    this.assignCompanyId = null;
    this.assignDepartmentFilterId = null;
    this.assignWorkerTypeId = null;
    this.assignShiftId = null;
    this.assignDepartmentForScheduleId = null;
    this.assignPage = 1;
    this.assignUsers = [];
    this.assignTotalCount = 0;
    this.assignTotalPages = 1;
    this.selectedEmployeeIds.clear();

    this.cdr.markForCheck();
    this.loadAssignFiltersAndUsers();
  }

  private loadAssignFiltersAndUsers(): void {
    this.assignIsLoadingFilters = true;
    this.assignIsLoadingUsers = true;
    this.cdr.markForCheck();

    this.schedulingService
      .getUserFilterValues()
      .pipe(
        tap((response) => {
          this.assignFilters = response?.data ?? null;
        }),
        catchError(() => {
          this.assignFilters = null;
          this.showToast('error', 'Failed to load filters.');
          return of(null);
        }),
        finalize(() => {
          this.assignIsLoadingFilters = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.loadAssignUsers());
  }

  private loadAssignUsers(): void {
    this.assignIsLoadingUsers = true;
    this.cdr.markForCheck();

    this.schedulingService
      .getUsers({
        page: this.assignPage,
        pageSize: this.assignPageSize,
        search: this.assignSearch.trim() || undefined,
        companyId: this.assignCompanyId,
        departmentId: this.assignDepartmentFilterId,
        workerTypeId: this.assignWorkerTypeId,
        shiftId: this.assignShiftId,
      })
      .pipe(
        tap((response) => {
          this.assignTotalCount = Number(response?.totalCount ?? 0) || 0;
          this.assignTotalPages = Number(response?.totalPages ?? 1) || 1;
          this.assignPage = Number(response?.currentPage ?? this.assignPage) || this.assignPage;
          this.assignUsers = response?.data ?? [];
        }),
        catchError(() => {
          this.assignUsers = [];
          this.showToast('error', 'Failed to load employees.');
          return of(null);
        }),
        finalize(() => {
          this.assignIsLoadingUsers = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  onAssignSearchChange(raw: string): void {
    this.assignSearch = raw ?? '';
    this.assignPage = 1;
    this.loadAssignUsers();
  }

  onAssignFilterChange(): void {
    this.assignPage = 1;
    this.loadAssignUsers();
  }

  onAssignDepartmentForScheduleChange(raw: string | number): void {
    const value = typeof raw === 'string' ? Number(raw) : raw;
    this.assignDepartmentForScheduleId = Number.isFinite(value) && value > 0 ? value : null;
    this.cdr.markForCheck();
  }

  goToAssignPage(page: number): void {
    const nextPage = Math.min(Math.max(1, page), this.assignTotalPages || 1);
    if (nextPage === this.assignPage) return;

    this.assignPage = nextPage;
    this.loadAssignUsers();
  }

  toggleEmployeeSelected(id: number, checked: boolean): void {
    if (!id) return;

    if (checked) {
      this.selectedEmployeeIds.add(id);
    } else {
      this.selectedEmployeeIds.delete(id);
    }

    this.cdr.markForCheck();
  }

  isEmployeeSelected(id: number): boolean {
    return this.selectedEmployeeIds.has(id);
  }

  selectAllOnPage(): void {
    for (const user of this.assignUsers) {
      this.selectedEmployeeIds.add(user.id);
    }

    this.cdr.markForCheck();
  }

  clearAllOnPage(): void {
    for (const user of this.assignUsers) {
      this.selectedEmployeeIds.delete(user.id);
    }

    this.cdr.markForCheck();
  }

  clearAllSelected(): void {
    this.selectedEmployeeIds.clear();
    this.cdr.markForCheck();
  }

  openCreateModal(): void {
    this.modalMode = 'create';
    this.isModalOpen = true;
    this.errorMessage = null;
    this.lockBodyScroll(true);

    this.createName = '';
    this.createShiftId = null;
    this.createDepartmentId = this.createDepartmentId ?? null;
    this.createDays = this.getDefaultCreateDays(false);

    this.cdr.markForCheck();
  }

  openRenameModal(): void {
    if (!this.selectedScheduleGroupDetails) return;

    this.modalMode = 'rename';
    this.isModalOpen = true;
    this.errorMessage = null;
    this.lockBodyScroll(true);

    this.renameOriginal = this.selectedScheduleGroupDetails.name ?? '';
    this.renameValue = this.renameOriginal;

    this.cdr.markForCheck();
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.errorMessage = null;

    if (this.modalMode === 'assign') {
      this.assignIsLoadingFilters = false;
      this.assignIsLoadingUsers = false;
    }

    this.lockBodyScroll(this.confirmOpen);
    this.cdr.markForCheck();
  }

  createScheduleGroup(): void {
    this.errorMessage = null;

    const name = this.createName.trim();
    if (!name) {
      this.showToast('error', 'Schedule name is required.');
      return;
    }

    if (!this.createShiftId) {
      this.showToast('error', 'Shift is required.');
      return;
    }

    if (!this.createDepartmentId) {
      this.showToast('error', 'Department is required.');
      return;
    }

    const scheduleTimeDays = this.createDays
      .filter((day) => day.enabled)
      .map((day) => ({
        dayOfWeek: day.dayOfWeek,
        startTime: day.startTime,
        endTime: day.endTime,
      }));

    if (!scheduleTimeDays.length) {
      this.showToast('error', 'Select at least one day.');
      return;
    }

    this.isSaving = true;
    this.cdr.markForCheck();

    const body: CreateScheduleGroupRequest = {
      name,
      departmentId: this.createDepartmentId,
      shiftId: this.createShiftId,
      timeZoneId: this.createTimeZoneId,
      scheduleTimeDays,
    };

    this.schedulingService
      .createScheduleGroup(body)
      .pipe(
        tap(() => {
          this.closeModal();
          this.showToast('success', 'Schedule created.');
        }),
        tap(() => this.loadScheduleGroups(true)),
        catchError(() => {
          this.showToast('error', 'Failed to create.');
          return of(null);
        }),
        finalize(() => {
          this.isSaving = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  onShiftChange(raw: string): void {
    this.createShiftId = raw ? parseInt(raw, 10) : null;
  }

  onCreateDepartmentChange(raw: string): void {
    this.createDepartmentId = raw ? parseInt(raw, 10) : null;
  }

  onCreateDayEnabledChange(index: number, checked: boolean): void {
    const current = this.createDays[index];
    if (!current) return;

    this.createDays[index] = { ...current, enabled: checked };
  }

  onCreateDayStartTimeChange(index: number, hhmm: string): void {
    const current = this.createDays[index];
    if (!current) return;

    this.createDays[index] = { ...current, startTime: `${hhmm}:00` };
  }

  onCreateDayEndTimeChange(index: number, hhmm: string): void {
    const current = this.createDays[index];
    if (!current) return;

    this.createDays[index] = { ...current, endTime: `${hhmm}:00` };
  }

  clearToast(): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    this.toastTimer = null;
    this.toastMessage = null;
    this.cdr.markForCheck();
  }

  private showToast(kind: ToastKind, message: string): void {
    this.toastKind = kind;
    this.toastMessage = message;

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    this.toastTimer = setTimeout(() => this.clearToast(), 2600);
    this.cdr.markForCheck();
  }

  private toRowVm(group: ScheduleGroupListItem): ScheduleGroupRowVm {
    const departments = (group.departments ?? []) as any[];
    const companies = ((group as any)?.companies ?? []) as any[];
    const primaryDepartment = departments[0] ?? null;
    const primaryCompany = companies[0] ?? (group as any)?.company ?? null;

    return {
      ...group,
      createdAtLabel: this.formatLocalDateTime(group.createdAt),
      createdByLabel:
        `${group.createdBy?.firstName ?? ''} ${group.createdBy?.lastName ?? ''}`.trim() || 'N/A',
      departmentLabel:
        departments
          .map((department) => department.name ?? department.code)
          .filter(Boolean)
          .join(', ') || 'N/A',
      primaryDepartmentId: this.toPositiveNumber(primaryDepartment?.id),
      primaryCompanyId: this.toPositiveNumber(primaryCompany?.id),
    };
  }

  private mapEmployeesFromDetails(details: ScheduleGroupDetails | null): EmployeeChipVm[] {
    const employees = (details as any)?.employees ?? [];

    return employees.map((employee: any) => ({
      id: employee.id,
      label:
        `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim() ||
        `Employee ${employee.id}`,
      employeeNumber: employee.employeeNumber ?? null,
      departmentId: this.toPositiveNumber(employee?.department?.id ?? employee?.departmentId),
      companyId: this.toPositiveNumber(employee?.company?.id ?? employee?.companyId),
    }));
  }

  getScheduleDuration(startTime: string | null | undefined, endTime: string | null | undefined): string {
    if (!startTime || !endTime || startTime === '—' || endTime === '—') {
      return '—';
    }

    const start = this.parseTimeToMinutes(startTime);
    const end = this.parseTimeToMinutes(endTime);

    if (start === null || end === null) {
      return '—';
    }

    const durationMinutes = end >= start ? end - start : end + 1440 - start;
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    if (minutes === 0) {
      return `${hours}h`;
    }

    return `${hours}h ${minutes}m`;
  }

  private parseTimeToMinutes(value: string): number | null {
    const match = value.trim().match(/^(\d{1,2}):(\d{2})/);

    if (!match) {
      return null;
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2]);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }

    return hours * 60 + minutes;
  }

  private buildCompactDayRows(details: ScheduleGroupDetails | null): DayRowVm[] {
    const rows = (details as any)?.scheduleWithDays ?? [];
    const byDay = new Map<DayOfWeekName, DayRowVm>();

    for (const row of rows) {
      const day = (row.dayofWeek ?? row.dayOfWeek) as DayOfWeekName;
      if (!day || byDay.has(day)) continue;

      const schedule =
        row.shiftSchedule?.schedule ??
        row.departmentShiftSchedule?.schedule ??
        row.schedule ??
        null;

      byDay.set(day, {
        id: row.id,
        dayOfWeek: day,
        startTime: this.hhmm(
          schedule?.localStartTime ??
          schedule?.startTime ??
          row.localStartTime ??
          row.startTime,
        ),
        endTime: this.hhmm(
          schedule?.localEndTime ??
          schedule?.endTime ??
          row.localEndTime ??
          row.endTime,
        ),
        isOverNight: !!(
          schedule?.isOverNight ??
          schedule?.isOvernight ??
          row.isOverNight ??
          row.isOvernight
        ),
      });
    }

    return DAY_ORDER.filter((day) => byDay.has(day)).map((day) => byDay.get(day)!);
  }

  private hhmm(value?: string): string {
    if (!value) return 'N/A';
    return value.length >= 5 ? value.slice(0, 5) : value;
  }

  private formatLocalDateTime(iso: string): string {
    if (!iso) return 'N/A';

    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;

    return date.toLocaleDateString([], {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  private getDefaultCreateDays(enabledByDefault: boolean): Array<{
    dayOfWeek: DayOfWeekName;
    startTime: string;
    endTime: string;
    enabled: boolean;
  }> {
    return DAY_ORDER.map((dayOfWeek) => ({
      dayOfWeek,
      startTime: '08:00:00',
      endTime: '16:00:00',
      enabled: enabledByDefault,
    }));
  }

  private emptyCurrentUser(): CurrentUser {
    return {
      id: 0,
      email: '',
      firstName: '',
      lastName: '',
      roles: [],
      rfid: null,
      employeeNumber: null,
      enableAutoBooking: false,
      department: null,
      shift: null,
      departmentScheduleGroup: null,
      company: null,
      jobTitle: null,
      reportTo: null,
      employees: [],
    };
  }

  private resetOpenDepartments(): void {
    this.openDepartmentIds.clear();
    this.cdr.markForCheck();
  }

  private setDefaultOpenDepartments(details: ScheduleGroupDetails | null): void {
    this.openDepartmentIds.clear();

    const departments = (details as any)?.departments ?? [];
    for (const department of departments) {
      const id = Number(department?.id ?? 0);
      const employeeCount = Array.isArray(department?.employees) ? department.employees.length : 0;

      if (id > 0 && employeeCount > 0) {
        this.openDepartmentIds.add(id);
      }
    }

    this.cdr.markForCheck();
  }

  getEmployeeColorClassById(
    id: number | string | null | undefined,
    employeeNumber?: string | number | null,
  ): string {
    return this.getStableColorClass('employee', id, employeeNumber);
  }

  getDepartmentColorClassById(id: number | string | null | undefined): string {
    return this.getStableColorClass('department', id);
  }

  getCompanyColorClassById(id: number | string | null | undefined): string {
    return this.getStableColorClass('company', id);
  }

  getScheduleColorClassById(id: number | string | null | undefined): string {
    return this.getStableColorClass('schedule', id);
  }

  getScheduleGroupColorClass(group: ScheduleGroupRowVm | ScheduleGroupListItem | null | undefined): string {
    return this.getScheduleColorClassById(group?.id);
  }

  getDepartmentColorClass(department: any): string {
    return this.getDepartmentColorClassById(department?.id);
  }

  getCompanyColorClass(company: any): string {
    return this.getCompanyColorClassById(company?.id);
  }

  getUserDepartmentColorClass(user: any): string {
    return this.getDepartmentColorClassById(user?.department?.id ?? user?.departmentId);
  }

  getUserCompanyColorClass(user: any): string {
    return this.getCompanyColorClassById(user?.company?.id ?? user?.companyId);
  }

  getUserDepartmentName(user: any): string {
    return user?.department?.name ?? user?.departmentName ?? '';
  }

  getUserCompanyName(user: any): string {
    return user?.company?.name ?? user?.companyName ?? '';
  }

  private getStableColorClass(
    entity: ColorEntity,
    id: number | string | null | undefined,
    fallback?: number | string | null,
  ): string {
    const index = this.getStableColorIndex(id, fallback);
    return `${entity}-color-${index}`;
  }

  private getStableColorIndex(
    id: number | string | null | undefined,
    fallback?: number | string | null,
  ): number {
    const idValue = this.toPositiveNumber(id);

    if (idValue !== null) {
      return idValue % COLOR_BUCKET_COUNT;
    }

    const fallbackValue = this.toPositiveNumber(fallback);

    if (fallbackValue !== null) {
      return fallbackValue % COLOR_BUCKET_COUNT;
    }

    const seed = String(id ?? fallback ?? '').trim();

    if (!seed) {
      return 0;
    }

    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
      hash = (hash * 31 + seed.charCodeAt(i)) | 0;
    }

    return Math.abs(hash) % COLOR_BUCKET_COUNT;
  }

  private toPositiveNumber(value: number | string | null | undefined): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.abs(Math.trunc(parsed)) : null;
  }

  trackEmployee(_: number, employee: EmployeeChipVm): number {
    return employee.id;
  }

  employeeInitials(user: UserRef): string {
    const parts = `${user.firstName ?? ''} ${user.lastName ?? ''}`
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (!parts.length) return '—';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  private lockBodyScroll(lock: boolean): void {
    const body = document.body;

    if (lock) {
      body.classList.add('modal-open');
    } else {
      body.classList.remove('modal-open');
    }
  }

  private isMissingDepartmentError(err: any): boolean {
    const status = Number(err?.status ?? err?.error?.status ?? 0);
    const raw = err?.error?.message ?? err?.error?.title ?? err?.message ?? err?.statusText ?? '';

    const text = String(raw).toLowerCase();
    const errors = err?.error?.errors;

    if (errors && typeof errors === 'object') {
      const keys = Object.keys(errors).map((key) => key.toLowerCase());

      const hasDepartmentIdKey = keys.some(
        (key) =>
          key === 'departmentid' || key.endsWith('.departmentid') || key.includes('departmentid'),
      );

      if (hasDepartmentIdKey) {
        const values = Object.values(errors).flatMap((value: any) =>
          Array.isArray(value) ? value : [value],
        );
        const joined = values.map((value) => String(value).toLowerCase()).join(' ');

        if (
          joined.includes('required') ||
          joined.includes('must not be empty') ||
          joined.includes('must not be null') ||
          joined.includes('cannot be null') ||
          joined.includes('is required')
        ) {
          return true;
        }

        return true;
      }
    }

    const mentionsDepartmentId = text.includes('departmentid') || text.includes('department id');

    if (!mentionsDepartmentId) {
      return false;
    }

    const isMissingLike =
      text.includes('required') ||
      text.includes('must not be empty') ||
      text.includes('must not be null') ||
      text.includes('cannot be null') ||
      text.includes('is required') ||
      text.includes('missing');

    return status === 400 ? isMissingLike : isMissingLike;
  }

  getEmployeeCount(): number {
    if (this.selectedScheduleGroupDetails?.departments?.length === 0) {
      return 0;
    }

    return (
      this.selectedScheduleGroupDetails?.departments
        .map((department) => department.employees.length)
        .reduce((sum, count) => sum + count, 0) ?? 0
    );
  }

  onScheduleSearchChange(value: string): void {
    this.scheduleSearch = value ?? '';
    this.scheduleSearchInput$.next(this.scheduleSearch);
  }

  clearScheduleSearch(): void {
    this.scheduleSearch = '';
    this.scheduleSearchInput$.next('');
    this.scheduleSearchTerm$.next('');
  }

  private bindScheduleSearch(): void {
    this.scheduleSearchInput$
      .pipe(debounceTime(1000), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.scheduleSearchTerm$.next(value ?? '');
        this.cdr.markForCheck();
      });
  }

  private filterScheduleGroups(
    rows: ScheduleGroupRowVm[],
    searchTerm: string,
  ): ScheduleGroupRowVm[] {
    const term = searchTerm.trim().toLowerCase();

    if (!term) return rows;

    return rows.filter((row) =>
      [
        row.name,
        row.departmentLabel,
        row.createdAtLabel,
        row.createdByLabel,
      ].some((value) => String(value ?? '').toLowerCase().includes(term)),
    );
  }
}
